import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import { postRequest } from '../../../helpers/apiHandler';
import {
  formatWithoutCurrency, truncateString,
} from '../../../helpers/helperFunction';
import CloseIcon from '../../SVG/CloseIcon';
import moment from 'moment';
import './_overview.scss';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const SmsDetailModal = ({ campaignId, dateRange, show, setCampaignId, setSmsDetailShow }) => {
  const { access_token } = useSelector((state) => state.auth.user);

  const [smsDetails, setSmsDetails] = useState([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [alertState, setAlertState] = useState({ message: '', show: false, variant: 'success' });

  const columns = [
    {
      name: 'Mobile Number',
      sortable: true,
      center: 'true',
      selector: (row) => row.phone_number,
    },
    {
      name: 'Message',
      sortable: true,
      center: 'true',
      width: '350px',
      selector: (row) => truncateString(row.message, 180),
    },
    {
      name: 'Message \nCount',
      sortable: true,
      center: 'true',
      selector: (row) => row.message_count,
    },
    {
      name: 'Status',
      sortable: true,
      center: 'true',
      selector: (row) => row.status,
    },
    {
      name: 'Country',
      sortable: true,
      center: 'true',
      selector: (row) => row.country,
    },
    {
      name: 'SMS \nRate',
      sortable: true,
      center: 'true',
      selector: (row) => formatWithoutCurrency(row.country_cost, 3),
    },
    {
      name: 'Campaign \nCost',
      sortable: true,
      center: 'true',
      selector: (row) => row.total_cost,
    },
  ];

  if (white_label_perkeso) columns.splice(5, 2); // REMOVES Campaign Cost & SMS Rate

  const fetch_sms_details = () => {
    const body = {
      date_range: dateRange,
      campaign_id: campaignId,
    };

    postRequest(`/v2/campaign-sms-details`, body, access_token.token, (res) => {
      if (res.success) {
        const details = res.data.sms_details;
        setSmsDetails(details);
      }
    });
  }

  const handleDownload = async () => {
    setLoadingDownload(true);
    try {
      // Delay to make the loadingDownload state sets in
      await new Promise((resolve) => setTimeout(resolve, 100));

      const headers = white_label_perkeso ? perkesoHeaders() : elfoHeaders();
      const headerRow = headers.map((header) => `"${header}"`).join(',');

      const rows = smsDetails.map((row) =>
        headers
          .map((header) => {
            let value = '';
            switch (header) {
              case 'Mobile Number':
                value = row.phone_number || '';
                break;
              case 'Country':
                value = row.country || '';
                break;
              case 'Message Count':
                value = row.message_count || 0;
                break;
              case 'Transaction ID':
                value = row.transaction_id || '';
                break;
              case 'Message':
                value = row.message || '';
                break;
              case 'SMS Rate':
                value = row.country_cost || 0;
                break;
              case 'Campaign Cost':
                value = row.total_cost || 0;
                break;
              case 'Status':
                value = row.status || '';
                break;
              default:
                value = '';
            }
            value = value.toString().replace(/[\r\n\t\f\v\s]+/g, ' ').trim();
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(',')
      );

      const csvContent = '\ufeff' + headerRow + '\n' + rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `SMS-Details-${moment().format('DD-MM-YYYY')}.csv`;

      document.body.appendChild(link);
      link.click();

      // Wait a bit before cleanup
      await new Promise((resolve) => setTimeout(resolve, 100));

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlertState({
        message: 'Your SMS details have been successfully exported to CSV.',
        show: true,
        variant: 'success',
      });
    } catch (error) {
      console.error('CSV generation error:', error);
      setAlertState({
        message: `Export failed: ${error.message || 'Unknown error occurred'}`,
        show: true,
        variant: 'danger',
      });
    } finally {
      // Add a small delay before removing loading state
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoadingDownload(false);
    }
  };

  const perkesoHeaders = () => {
    return [
      'Mobile Number',
      'Country',
      'Message Count',
      'Transaction ID',
      'Message',
      'Status'
    ];
  };

  const elfoHeaders = () => {
    return [
      'Mobile Number',
      'Country',
      'Message Count',
      'Transaction ID',
      'Message',
      'SMS Rate',
      'Campaign Cost',
      'Status'
    ];
  };

  useEffect(() => {
    if (show) {
      fetch_sms_details()
    }
  }, [show]);

  const handleClose = () => {
    setCampaignId(null);
    setSmsDetailShow(false);
    setSmsDetails([]);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Body className='sms-detail-modal'>
        <CloseIcon handleModalClose={handleClose} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 16px 8px 8px' }}>
          <Modal.Title as="h4">
            SMS Campaign Details
          </Modal.Title>
          <Button
            variant="primary"
            disabled={loadingDownload}
            style={{ minWidth: 160 }}
            onClick={handleDownload}
          >
            {loadingDownload ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </div>
        <DataTable columns={columns} data={smsDetails} pagination />
      </Modal.Body>
    </Modal>
  );
};

export default SmsDetailModal;
